import { getRecord, insertRecord, updateRecord } from "@/utils/helpers/data";
import { TABLES } from "@/utils/constants";

export const COLUMNS = {
  ID: "id",
  CREATED_AT: "created_at",
  NOTIFICATION_TOKENS: "notification_tokens",
};

export interface User {
  id: string;
  created_at?: string;
  notification_tokens: string | null;
}

/**
 * Retrieves a single user by its ID.
 * @param userId - The ID of the user to retrieve.
 * @returns The requested user or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getUser = async (
  userId: string | null | undefined,
): Promise<User | null> => {
  if (!userId) return null;
  return getRecord<User>(TABLES.USERS, COLUMNS.ID, userId);
};
/**
 * Creates a new user record in the database.
 * @param user - The user data to insert.
 * @returns The created user or null if insertion failed.
 * @throws Will throw an error if the insertion fails.
 */
export const createUser = async (user: User): Promise<User | null> => {
  return insertRecord<User>(TABLES.USERS, user);
};

/**
 * Uploads a notification token for a specific user.
 * @param userId - The ID of the user.
 * @param token - The notification token to upload.
 * @returns The updated user, or null if the update failed.
 * @throws Will throw an error if the update fails.
 */
export const uploadNotificationToken = async ({
  userId,
  token,
}: {
  userId: string | null | undefined;
  token: string | null | undefined;
}): Promise<User | null> => {
  if (!userId || !token) return null;

  try {
    const user = await getUser(userId);

    if (user) {
      const existingTokens = user.notification_tokens
        ? user.notification_tokens.split(",")
        : [];

      if (existingTokens.includes(token)) return null;

      existingTokens.push(token);

      const tokens = existingTokens.join(",");

      return updateRecord<User>(TABLES.USERS, COLUMNS.ID, userId, {
        [COLUMNS.NOTIFICATION_TOKENS]: tokens,
      });
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Removes a specific notification token for a user.
 * @param userId - The ID of the user.
 * @param token - The specific token to remove.
 * @returns The updated user, or null if the update failed.
 */
export const removeNotificationToken = async ({
  userId,
  token,
}: {
  userId: string | null | undefined;
  token: string | null | undefined;
}): Promise<User | null> => {
  if (!userId || !token) return null;

  try {
    const user = await getUser(userId);

    if (user && user.notification_tokens) {
      const existingTokens = user.notification_tokens.split(",");
      const filteredTokens = existingTokens.filter((existingToken) =>
        existingToken !== token
      );

      const updatedTokens = filteredTokens.length > 0
        ? filteredTokens.join(",")
        : null;

      return updateRecord<User>(TABLES.USERS, COLUMNS.ID, userId, {
        [COLUMNS.NOTIFICATION_TOKENS]: updatedTokens,
      });
    }

    return null;
  } catch {
    return null;
  }
};
